import { Link } from "react-router-dom";
import { Heart, ShoppingBag, X } from "lucide-react";
import { useWishlist } from "@/context/WishlistContext";
import { useCart } from "@/context/CartContext";
import { Stars } from "@/components/Stars";
import { Reveal } from "@/components/Reveal";

export default function Wishlist() {
  const { items, remove } = useWishlist();
  const { addItem } = useCart();

  return (
    <div className="min-h-screen bg-ivory px-5 py-16 md:px-10 md:py-24" data-testid="wishlist-page">
      <div className="mx-auto max-w-[1200px]">
        <p className="eyebrow text-gold">Saved For Later</p>
        <h1 className="mt-3 font-serif text-5xl font-light tracking-tight text-ink md:text-7xl">Wishlist</h1>

        {items.length === 0 ? (
          <Reveal className="mt-16 flex flex-col items-center gap-6 text-center">
            <Heart size={44} strokeWidth={1} className="text-sand" />
            <p className="text-ink/60">Your wishlist is empty. Tap the heart on any piece to save it.</p>
            <Link to="/shop" className="rounded-full bg-ink px-9 py-4 text-[0.72rem] uppercase tracking-[0.24em] text-ivory transition-colors hover:bg-gold">
              Explore Jewelry
            </Link>
          </Reveal>
        ) : (
          <div className="mt-12 grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-4 md:gap-x-6">
            {items.map((p) => (
              <div key={p.slug} className="group relative" data-testid={`wishlist-item-${p.slug}`}>
                <div className="relative overflow-hidden rounded-2xl bg-beige aspect-[4/5]">
                  <Link to={`/product/${p.slug}`}>
                    <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                  </Link>
                  <button
                    onClick={() => remove(p.slug)}
                    className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-ivory/85 backdrop-blur"
                    data-testid={`wishlist-remove-${p.slug}`}
                  >
                    <X size={16} strokeWidth={1.5} />
                  </button>
                  <div className="absolute inset-x-3 bottom-3 translate-y-4 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    <button
                      onClick={() => addItem(p)}
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3 text-[0.7rem] uppercase tracking-[0.22em] text-ivory transition-colors hover:bg-gold"
                      data-testid={`wishlist-add-${p.slug}`}
                    >
                      <ShoppingBag size={14} /> Add to Bag
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex items-start justify-between">
                  <div>
                    <Link to={`/product/${p.slug}`} className="font-serif text-xl text-ink">{p.name}</Link>
                    <Stars rating={p.rating} className="mt-1" />
                  </div>
                  <span className="text-sm text-ink">${p.price?.toFixed(0)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
